import React, { useState, useEffect, useReducer } from "react"
import Grid from "@kit/design/grid"
import Stack from "@kit/design/stack"
import Input from "@kit/design/input"
import DatePickerInput from "@kit/design/date-picker-input"
import SelectMenu from "@kit/design/select-menu"
import { Field } from "core/form"
import { ColorInput } from "core/color-picker"
import { useCurrentUser } from "hooks/use-current-user"
import { useFetch } from "hooks/use-new-fetch"
import { useTrailingDebounce } from "hooks/use-trailing-debounce"

// Timezones
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz"
import { timezoneName } from "utils/date"

const getData = (node, key, defaultValue) => node.data.get(key) ?? defaultValue

const STYLES = [
  { value: "plain", label: "Plain", background: "#ffffff", color: "#32404A" },
  {
    value: "squared",
    label: "Squared",
    background: "#ffffff",
    color: "#32404A"
  },
  {
    value: "rounded",
    label: "Rounded",
    background: "#ffffff",
    color: "#2D58F1"
  }
]

const getDefaultStyle = (property, themeState) => {
  const themeStyles = STYLES.find(style => style.value === themeState)
  return themeStyles ? themeStyles[property] : ""
}

function timerReducer(state, action) {
  const { type, payload } = action
  switch (type) {
    case "url":
      return { ...state, settings: { ...state.settings, url: payload } }
    case "endMessage":
      return { ...state, settings: { ...state.settings, endMessage: payload } }
    case "theme":
      return { ...state, settings: { ...state.settings, theme: payload } }
    case "color":
      return { ...state, settings: { ...state.settings, color: payload } }
    case "background":
      return { ...state, settings: { ...state.settings, background: payload } }
    case "deadline":
      return { ...state, deadline: payload }

    default:
      return state
  }
}

export const TimerMenu = ({ editor, node }) => {
  const user = useCurrentUser()
  const slug = getData(node, "slug")
  const [request] = useFetch(`/pages/timer/${slug}`)

  const [loadingChange, setLoadingChange] = useState(false)
  const [state, dispatch] = useReducer(timerReducer, {
    settings: {
      color: "",
      background: "",
      endMessage: "",
      theme: "",
      url: ""
    },
    deadline: "",
    slug
  })
  const {
    settings: { color, background, theme, endMessage, url }
  } = state
  const defaultBackground = getDefaultStyle("background", theme)
  const defaultColor = getDefaultStyle("color", theme)

  const parent = editor.value.document.getParent(node.key)
  const block = parent?.type === "captioned-node" ? parent : node

  // Used to store the deadline in the user's timezone
  const [zonedDeadline, setZonedDeadline] = useState("")

  const handleDateChange = event => {
    const localDate = event.target.value
    // The timezone from JS will be in the local timezone, so we need to convert it back to UTC
    // but as if the date was in the account timezone
    const utcDate = zonedTimeToUtc(localDate, user.timezone)
    dispatch({ type: "deadline", payload: utcDate })
    setZonedDeadline(utcToZonedTime(utcDate, user.timezone))
    setBlockData({ target: { name: "deadline", value: utcDate } })
  }

  const [debouncedOnChange] = useTrailingDebounce(() => {
    updateTimerRecord()
  }, 100)

  const setBlockData = event => {
    const { name, value } = event.target
    setLoadingChange(true)
    dispatch({ type: name, payload: value })
    debouncedOnChange()
  }

  const updateTimerRecord = async () => {
    await request.put({ data: state })
    const data = block.data.toJS()
    const version = data.version || 1
    editor.command("setNodeByKey", block.key, {
      data: { ...data, version: version + 1, url }
    })
    setLoadingChange(false)
  }

  useEffect(() => {
    const getTimerSettings = async () => {
      const response = await request.get()
      const { data } = response.data
      const { settings } = data

      dispatch({ type: "deadline", payload: data.deadline })
      setZonedDeadline(utcToZonedTime(data.deadline, user.timezone))
      dispatch({ type: "theme", payload: settings.theme })
      dispatch({ type: "endMessage", payload: settings.endMessage })
      dispatch({ type: "url", payload: settings.url })
      dispatch({ type: "background", payload: settings.background })
      dispatch({ type: "color", payload: settings.color })
    }
    getTimerSettings()
  }, [slug, user.timezone])

  return (
    <Stack spacing={1}>
      <Field
        control={DatePickerInput}
        label="End date"
        name="deadline"
        selected={zonedDeadline}
        onChange={handleDateChange}
        controlClassName="w-full"
      />
      <p className="text-xs text-gray-700">
        Timezone: {timezoneName(zonedDeadline, user.timezone)}
      </p>
      <Field
        control={Input}
        label="URL"
        name="url"
        value={url}
        onChange={setBlockData}
        placeholder={"https://kit.com"}
      />
      <Field
        control={Input}
        label="Display this when the timer ends"
        name="endMessage"
        value={endMessage}
        onChange={setBlockData}
        maxLength="60"
        placeholder="e.g Sale has ended"
        className="pt-2"
      />
      <div className="text-right text-gray-700">{endMessage.length}/60</div>
      <Field
        control={SelectMenu}
        label="Style"
        name="theme"
        controlClassName="w-full"
        onChange={setBlockData}
        options={STYLES}
        value={theme}
      />
      <Grid
        columns="repeat(2, 1fr)"
        gap=".5rem"
        rowGap=".75rem"
        className="pt-4"
      >
        <Field
          control={ColorInput}
          label="Background color"
          name="background"
          placement="top"
          onChange={setBlockData}
          value={background || defaultBackground}
        />
        <Field
          control={ColorInput}
          label="Foreground color"
          name="color"
          placement="top"
          onChange={setBlockData}
          value={color || defaultColor}
        />
      </Grid>
      <p className="pt-2 text-gray-700">
        {loadingChange && "Applying changes..."}
      </p>
    </Stack>
  )
}

